import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getStatusConfig, USER_STATUSES, USER_STATUS_KEYS, type UserStatusKey } from "@shared/status-config";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronUp, ChevronDown, Timer, Clock } from "lucide-react";

interface StatusBubbleProps {
  userId: number;
  size?: "sm" | "md" | "lg";
  showTooltip?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "text-base w-6 h-6",
  md: "text-lg w-7 h-7",
  lg: "text-xl w-8 h-8",
};

export function StatusBubble({ userId, size = "sm", showTooltip = true, className = "" }: StatusBubbleProps) {
  const { data: statuses } = useQuery<{ id: number; username: string; availabilityStatus: string; statusExpiresAt?: string | null }[]>({
    queryKey: ["/api/users/availability-statuses"],
    refetchInterval: 60000,
  });

  const userStatus = statuses?.find((s) => s.id === userId);
  const config = getStatusConfig(userStatus?.availabilityStatus);

  if (config.isHidden) return null;

  const expiresAt = userStatus?.statusExpiresAt;
  const expiresDate = expiresAt ? new Date(expiresAt) : null;
  const isFutureExpiry = expiresDate && expiresDate > new Date();
  const tooltipLabel = isFutureExpiry
    ? `${config.label} — until ${expiresDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`
    : config.label;

  const bubble = (
    <span
      className={`inline-flex items-center justify-center rounded-full leading-none select-none ${sizeClasses[size]} ${className}`}
      style={{ backgroundColor: `${config.color}22` }}
    >
      {config.emoji}
    </span>
  );

  if (!showTooltip) return bubble;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{bubble}</TooltipTrigger>
        <TooltipContent side="top" className="bg-gray-800 text-gray-200 text-xs border-gray-600">
          {tooltipLabel}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface StatusPickerProps {
  currentStatus: string;
  onSelect: (status: UserStatusKey, timerMinutes?: number) => void;
  savedTimers?: Record<string, number>;
  statusExpiresAt?: string | null;
}

const TIMER_STATUSES: UserStatusKey[] = ['focus', 'rendering', 'lunch', 'afk', 'meeting'];
const DEFAULT_TIMERS: Record<string, number> = {
  focus: 120,
  rendering: 60,
  lunch: 30,
  afk: 5,
  meeting: 60,
};

function minutesToHM(total: number): [number, number] {
  return [Math.floor(total / 60), total % 60];
}

function getDefaultUntilTime(): string {
  const now = new Date();
  const totalMins = now.getHours() * 60 + now.getMinutes();
  const nextQuarter = Math.ceil((totalMins + 1) / 15) * 15;
  const h = Math.floor(nextQuarter / 60) % 24;
  const m = nextQuarter % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function parseTimeInput(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return null;

  let hours: number;
  let minutes: number;

  if (digits.length <= 2) {
    hours = parseInt(digits, 10);
    minutes = 0;
  } else if (digits.length === 3) {
    hours = parseInt(digits[0], 10);
    minutes = parseInt(digits.slice(1), 10);
  } else {
    hours = parseInt(digits.slice(0, 2), 10);
    minutes = parseInt(digits.slice(2, 4), 10);
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function untilTimeToMinutes(timeStr: string): number | null {
  const parsed = parseTimeInput(timeStr);
  if (!parsed) return null;
  const parts = parsed.split(':');
  if (parts.length !== 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;

  const now = new Date();
  const target = new Date(now);
  target.setHours(h, m, 0, 0);

  if (target <= now) {
    target.setDate(target.getDate() + 1);
  }

  const diffMs = target.getTime() - now.getTime();
  return Math.max(1, Math.round(diffMs / 60000));
}

export function StatusPicker({ currentStatus, onSelect, savedTimers = {}, statusExpiresAt }: StatusPickerProps) {
  const expiresAtTime = useMemo(() => {
    if (!statusExpiresAt) return null;
    const d = new Date(statusExpiresAt);
    if (isNaN(d.getTime()) || d.getTime() <= Date.now()) return null;
    if (d.getSeconds() >= 30) {
      d.setMinutes(d.getMinutes() + 1, 0, 0);
    }
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }, [statusExpiresAt]);

  const [timers, setTimers] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    for (const key of TIMER_STATUSES) {
      init[key] = savedTimers[key] || DEFAULT_TIMERS[key] || 30;
    }
    return init;
  });

  const [defaultUntil, setDefaultUntil] = useState(() => getDefaultUntilTime());

  const [untilTimes, setUntilTimes] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    const fallback = getDefaultUntilTime();
    for (const key of TIMER_STATUSES) {
      if (key === currentStatus && expiresAtTime) {
        init[key] = expiresAtTime;
      } else {
        init[key] = fallback;
      }
    }
    return init;
  });

  const [useUntil, setUseUntil] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const key of TIMER_STATUSES) {
      init[key] = key === currentStatus && !!expiresAtTime;
    }
    return init;
  });

  const stopEvent = useCallback((e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const adjustTimer = useCallback((statusKey: string, field: 'hours' | 'minutes', delta: number, e: React.MouseEvent) => {
    stopEvent(e);
    setUseUntil(prev => ({ ...prev, [statusKey]: false }));
    setTimers(prev => {
      const current = prev[statusKey] || 30;
      const [h, m] = minutesToHM(current);
      let newH = h;
      let newM = m;
      if (field === 'hours') {
        newH = h + delta;
        if (newH < 0) newH = 23;
        if (newH > 23) newH = 0;
      } else {
        newM = m + delta;
        if (newM < 0) newM = 55;
        if (newM > 55) newM = 0;
      }
      return { ...prev, [statusKey]: newH * 60 + newM };
    });
  }, [stopEvent]);

  const handleUntilChange = useCallback((statusKey: string, value: string) => {
    setUntilTimes(prev => ({ ...prev, [statusKey]: value }));
    setUseUntil(prev => ({ ...prev, [statusKey]: true }));
  }, []);

  const handleUntilBlur = useCallback((statusKey: string) => {
    setUntilTimes(prev => {
      const raw = prev[statusKey] || '';
      const parsed = parseTimeInput(raw);
      return { ...prev, [statusKey]: parsed || defaultUntil };
    });
  }, [defaultUntil]);

  const getEffectiveMinutes = useCallback((key: string): number => {
    if (useUntil[key]) {
      const mins = untilTimeToMinutes(untilTimes[key] || defaultUntil);
      if (mins !== null && mins > 0) return mins;
    }
    return timers[key] || 0;
  }, [useUntil, untilTimes, timers, defaultUntil]);

  return (
    <div className="grid grid-cols-1 gap-0.5 py-1">
      {USER_STATUS_KEYS.map((key) => {
        const config = USER_STATUSES[key];
        const isActive = currentStatus === key;
        const hasTimer = TIMER_STATUSES.includes(key);
        const totalMin = timers[key] || 0;
        const [h, m] = minutesToHM(totalMin);
        const isUntilActive = useUntil[key] || false;

        return (
          <div
            key={key}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors w-full cursor-pointer
              ${isActive ? "bg-gray-600 text-white" : "text-gray-300 hover:bg-gray-700 hover:text-white"}`}
            onClick={() => {
              if (hasTimer) {
                const mins = getEffectiveMinutes(key);
                if (mins > 0) {
                  onSelect(key, mins);
                } else {
                  onSelect(key);
                }
              } else {
                onSelect(key);
              }
            }}
          >
            <span className="text-base">{config.emoji}</span>
            <span className="flex-1 text-sm font-medium">{config.label}</span>
            {hasTimer && (
              <div
                className="flex items-center gap-1.5 ml-auto"
                onClick={stopEvent}
                onMouseDown={stopEvent}
              >
                <Timer className={`w-3.5 h-3.5 ${isUntilActive ? 'text-gray-600' : 'text-gray-400'}`} />
                <div className={`flex items-center gap-1 ${isUntilActive ? 'opacity-40' : ''}`}>
                  <div className="flex flex-col items-center">
                    <button
                      className="text-gray-400 hover:text-white p-0 leading-none"
                      onClick={(e) => adjustTimer(key, 'hours', 1, e)}
                      onMouseDown={stopEvent}
                      tabIndex={-1}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-6 text-center text-sm font-mono tabular-nums text-gray-200 font-semibold">
                      {String(h).padStart(2, '0')}
                    </span>
                    <button
                      className="text-gray-400 hover:text-white p-0 leading-none"
                      onClick={(e) => adjustTimer(key, 'hours', -1, e)}
                      onMouseDown={stopEvent}
                      tabIndex={-1}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <span className="text-gray-400 text-sm font-semibold">:</span>
                  <div className="flex flex-col items-center">
                    <button
                      className="text-gray-400 hover:text-white p-0 leading-none"
                      onClick={(e) => adjustTimer(key, 'minutes', 5, e)}
                      onMouseDown={stopEvent}
                      tabIndex={-1}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-6 text-center text-sm font-mono tabular-nums text-gray-200 font-semibold">
                      {String(m).padStart(2, '0')}
                    </span>
                    <button
                      className="text-gray-400 hover:text-white p-0 leading-none"
                      onClick={(e) => adjustTimer(key, 'minutes', -5, e)}
                      onMouseDown={stopEvent}
                      tabIndex={-1}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="w-px h-8 bg-gray-600 mx-1" />

                <Clock className={`w-3.5 h-3.5 ${isUntilActive ? 'text-gray-300' : 'text-gray-600'}`} />
                <input
                  type="text"
                  value={untilTimes[key] ?? defaultUntil}
                  placeholder={defaultUntil}
                  onChange={(e) => handleUntilChange(key, e.target.value)}
                  onBlur={() => handleUntilBlur(key)}
                  onFocus={() => {
                    setUntilTimes(prev => ({ ...prev, [key]: '' }));
                    setUseUntil(prev => ({ ...prev, [key]: true }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      const raw = untilTimes[key] || '';
                      const parsed = parseTimeInput(raw);
                      if (parsed) {
                        setUntilTimes(prev => ({ ...prev, [key]: parsed }));
                        const mins = untilTimeToMinutes(parsed);
                        if (mins && mins > 0) {
                          onSelect(key, mins);
                        }
                      }
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`w-[3.2rem] text-center text-sm font-mono tabular-nums font-semibold bg-gray-700 border rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-gray-600 ${
                    isUntilActive
                      ? 'text-gray-100 border-gray-500'
                      : 'text-gray-400 border-gray-600'
                  }`}
                  maxLength={5}
                  tabIndex={-1}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
